/* eslint-disable no-magic-numbers */
import React, { useContext } from 'react';
import { get, isArray } from 'lodash';

import { TNEMessageData, thisNumberExplainedMessage } from '@futrli/backend-messages';
import { IntlShape, useIntl } from '@futrli/localisation';

import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';
import { RowType, RowsType } from '../reports/data-grid/types';
import { isAiAssumption } from '../reports/data-grid/headers/header-row-utils';
import { RowHeights } from '../reports/user-grid-settings/types';
import { getRowHeight } from '../reports/data-grid/style-rows';
import * as styles from './overlay-explanations-container.scss';

interface Props {
  rows: RowsType;
  showPredictionsExplanation?: boolean;
  rowHeaderWidth?: number;
  rowHeights: RowHeights;
}

const EXPLAIN_TEXT_LEFT_MARGIN = 32;
const EXPLAIN_TEXT_LENGTH = 18;

export const OverlayExplanationsContainer = ({
  rows,
  showPredictionsExplanation,
  rowHeaderWidth,
  rowHeights
}: Props) => {
  const intl = useIntl();

  // If this component is ever used in the context of consolidated forecasts, we
  // may need to get the currency code from the consolidation instead.
  const { currency } = useContext(PredictManagementContext);

  if (!showPredictionsExplanation) {
    return null;
  }

  const eachRowHeight: number[] = [];

  for (let i = 0; i < rows.length; i++) {
    eachRowHeight.push(getRowHeight(i, rowHeights, rows[i], true));
  }

  return (
    <>
      {rows.map((row: RowType, index: number) => {
        if (!isAiAssumption(row)) {
          return null;
        }

        const rowHeight = getRowHeight(index, rowHeights, rows[index], true);
        const summaryText = getSummaryText(row, intl, currency);
        const rowHeightsBeforeCurrentRow = eachRowHeight.slice(0, index);
        const heightBeforeCurrentRow = rowHeightsBeforeCurrentRow.reduce(
          (accumulator, currentNumber) => accumulator + currentNumber,
          0
        );

        return (
          <div
            key={`${row.account.index}-${index}`}
            className={styles.explainTextDataGrid}
            style={{
              position: 'absolute',
              left: typeof rowHeaderWidth === 'number' ? rowHeaderWidth + EXPLAIN_TEXT_LEFT_MARGIN : '',
              top: heightBeforeCurrentRow + (rowHeight - EXPLAIN_TEXT_LENGTH) / 2
            }}
          >
            {summaryText}
          </div>
        );
      })}
    </>
  );
};


export function getSummaryText(
  row: RowType,
  intl: IntlShape,
  currency_code: string | null | undefined,
): JSX.Element | null {
  // Display newer translatable summary messages, if available (with the correct config)
  if (isValidCurrencyCode(currency_code)) {
    const summaryMessages = get(row, 'account.this_number_explained.summary_messages', null) as (TNEMessageData[] | null);
    if (isArray(summaryMessages)) {
      const tneMessages = summaryMessages.map(
        msg => {
          return thisNumberExplainedMessage(msg.id, msg.values, intl, currency_code as string);
        }
      );

      return (<>{tneMessages}</>);
    }
  }

  // Fall back to older backend-supplied un-translatable summary text, if available
  const summaryText = get(row, 'account.this_number_explained.summary_text', null);
  if (summaryText !== null ) {
    return (<>{ summaryText }</>);
  }

  // No messages or text were supplied by the backend for this row
  return null
}


export function isValidCurrencyCode(currency_code: string | null | undefined): boolean {
  return !!(currency_code || '').trim()
}
